import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import {
  selectDashboardDateRangeSelection,
  selectDashboardScope,
  selectDashboardResultType,
  selectDashboardListOrderBy,
  selectDashboardPerScopeValueList,
  setDashboardScope,
  setDashboardResultType,
} from '@reducers/v2/dashboard/dashboard.slice';
import { DashboardScope, DashboardResultType } from '@api/domain/types';
import type { DashboardResult } from '@api/domain/types';
import { useGetDashboardOverall, useGetDashboardPerScopeValueList } from '@api/modules/dashboard/hooks';
import { getDashboardEntityScreenTarget } from '@v2/pages/dashboard/common/helpers';
import { useDashboardScreenTopOffset } from '@v2/pages/dashboard/common/hooks/useDashboardScreenTopOffset';
import { SHOW_ALL_LAYOUT } from '@v2/pages/dashboard/modules/showAll/components/DashboardShowAll/constants';

export const useDashboardShowAllViewModel = () => {
  const dispatch = useAppDispatch();
  const navigation = useNavigation<any>();
  const { from, to } = useSelector(selectDashboardDateRangeSelection);
  const scope = useSelector(selectDashboardScope);
  const resultType = useSelector(selectDashboardResultType);
  const orderBy = useSelector(selectDashboardListOrderBy);
  const { pageNumber } = useSelector(selectDashboardPerScopeValueList);

  const isDiverging = resultType !== DashboardResultType.Actual;

  const { data: overall, loading: overallLoading } = useGetDashboardOverall({ scope, resultType, from, to, enabled: isDiverging });
  const list = useGetDashboardPerScopeValueList({ scope, resultType, from, to, orderBy });

  const onSelectScope = useCallback((next: DashboardScope) => dispatch(setDashboardScope(next)), [dispatch]);
  const onSelectResultType = useCallback((next: DashboardResultType) => dispatch(setDashboardResultType(next)), [dispatch]);

  const onRowPress = useCallback(
    (item: DashboardResult) => {
      const target = getDashboardEntityScreenTarget(scope, item);
      if (!target) return;
      navigation.navigate(target.screenName as never, target.params as never);
    },
    [navigation, scope]
  );

  const topOffset = useDashboardScreenTopOffset(SHOW_ALL_LAYOUT.header.marginTop);

  return {
    scope,
    resultType,
    orderBy,
    from,
    to,
    pageNumber,
    overall,
    overallLoading,
    list,
    onSelectScope,
    onSelectResultType,
    onRowPress,
    topOffset,
  };
};
